import React, { useState, useEffect } from "react";
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
} from "react-native";
import { Toast } from "react-native-toast-notifications";
import { router, useLocalSearchParams } from "expo-router";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import customAxios from "../../utils/customAxios";
import i18n from "../../locales/i18n";
import WebSidebar from "./WebSidebar";

const WebDetteDetailsScreen: React.FC = () => {
  const { locale } = useSelector((state: RootState) => state.app);
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [phone, setPhone] = useState("");
  const [paidAmount, setPaidAmount] = useState(0);
  const [editPaidAmount, setEditPaidAmount] = useState("");
  const [edit, setEdit] = useState(false);
  const [paymentEdit, setPaymentEdit] = useState(false);
  const [extractedValue, setExtractedValue] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [buttonSpinner, setButtonSpinner] = useState(false);
  const [deleteSpinner, setDeleteSpinner] = useState(false);
  const [bill, setBill] = useState<any>(null);
  const [createdAt, setCreatedAt] = useState("");
  const [originalData, setOriginalData] = useState({
    name: "",
    amount: "",
    phone: "",
    paidamount: 0,
  });

  // Check if current language is RTL (Arabic)
  const isRTL = locale === "ar" || i18n.locale === "ar";

  const { id } = useLocalSearchParams();

  const totalAmount = parseFloat(amount) || 0;
  const remainingAmount = totalAmount - paidAmount;
  const paymentPercentage =
    totalAmount > 0 ? (paidAmount / totalAmount) * 100 : 0;

  const getPaymentStatus = () => {
    if (remainingAmount <= 0) return "paid";
    if (paidAmount > 0) return "partial";
    return "unpaid";
  };

  const getStatusColor = () => {
    const status = getPaymentStatus();
    switch (status) {
      case "paid":
        return "#22C55E";
      case "partial":
        return "#F59E0B";
      case "unpaid":
        return "#EF4444";
      default:
        return "#6B7280";
    }
  };

  useEffect(() => {
    const fetchDebt = async () => {
      try {
        const res = await customAxios.get(`/debt/${id}`, {
          withCredentials: true,
        });
        const data = res.data;

        setName(data?.fullname || "");
        setAmount(data?.amount?.toString() || "0");
        setPhone(data?.phone || "");
        setBill(data?.Facture || null);
        setCreatedAt(data?.createdAt || "");

        const paid = Number(data?.paidamount) || 0;
        setPaidAmount(paid);
        setEditPaidAmount(paid.toString());

        setOriginalData({
          name: data?.fullname || "",
          amount: data?.amount?.toString() || "0",
          phone: data?.phone || "",
          paidamount: paid,
        });
      } catch (error) {
        Toast.show(i18n.t("errorOccurred"), { type: "danger" });
        router.back();
      } finally {
        setIsLoading(false);
      }
    };

    fetchDebt();
  }, [id]);

  const handleUpdateDebt = async () => {
    if (!name.trim() || !amount || Number(amount) <= 0) {
      Toast.show(i18n.t("fillall"), {
        type: "danger",
      });
      return;
    }

    if (phone && !/^\d{10}$/.test(phone.replace(/[\s-]/g, ""))) {
      Toast.show(i18n.t("invalidPhoneNumber"), {
        type: "danger",
      });
      return;
    }

    setButtonSpinner(true);
    try {
      await customAxios.patch(`/debt/edit/${id}`, {
        fullname: name.trim(),
        amount: Number(amount),
        phone: phone.trim(),
        paidamount: Number(editPaidAmount) || 0,
      });

      Toast.show(i18n.t("updateSuccess"), {
        type: "success",
      });
      setPaidAmount(Number(editPaidAmount) || 0);
      setOriginalData({
        name,
        amount,
        phone,
        paidamount: Number(editPaidAmount) || 0,
      });
      setEdit(false);
    } catch (error) {
      Toast.show(i18n.t("updateFailed"), { type: "danger" });
    } finally {
      setButtonSpinner(false);
    }
  };

  const handlePayment = async () => {
    if (!extractedValue || Number(extractedValue) <= 0) {
      Toast.show(i18n.t("validevalueerror"), {
        type: "danger",
      });
      return;
    }

    setButtonSpinner(true);
    try {
      await customAxios.patch(`/debt/pay/${id}`, {
        paidamount: Number(extractedValue),
      });

      const newTotal = paidAmount + Number(extractedValue);
      setPaidAmount(newTotal);
      setEditPaidAmount(newTotal.toString());
      setOriginalData((prev) => ({ ...prev, paidamount: newTotal }));

      Toast.show(i18n.t("dettehasbeenpaid"), {
        type: "success",
      });
      setPaymentEdit(false);
      setExtractedValue("");
    } catch (error) {
      Toast.show(i18n.t("errorwhilepaying"), {
        type: "danger",
      });
    } finally {
      setButtonSpinner(false);
    }
  };

  const handleDelete = async () => {
    setDeleteSpinner(true);
    try {
      await customAxios.delete(`/debt/${id}`);
      Toast.show(i18n.t("dettehasbeendeleted"), {
        type: "success",
      });
      router.push("/dettes");
    } catch (error) {
      Toast.show(i18n.t("errorOccurred"), { type: "danger" });
    } finally {
      setDeleteSpinner(false);
    }
  };

  const handleCancelEdit = () => {
    setName(originalData.name);
    setAmount(originalData.amount);
    setPhone(originalData.phone);
    setEditPaidAmount(originalData.paidamount.toString());
    setEdit(false);
  };

  if (isLoading) {
    return (
      <div
        key={`dette-details-loading-${locale}`}
        className="flex flex-1 bg-[#F5F5F5] min-h-screen flex-row"
      >
        <WebSidebar />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#1379A2" />
        </View>
      </div>
    );
  }

  return (
    <div
      key={`dette-details-${locale}`}
      className="flex flex-1 bg-[#F5F5F5] min-h-screen flex-row"
    >
      <WebSidebar />

      <View style={styles.mainContent}>
        <ScrollView
          style={styles.scrollView}
          showsVerticalScrollIndicator={false}
        >
          {/* Header */}
          <div
            className={`flex flex-row items-center justify-between ${isRTL ? "flex-row-reverse" : ""}`}
          >
            <Text style={styles.headerTitle}>
              {i18n.t("credit")} {"  >  "} {name}
            </Text>
            <View style={styles.headerIconContainer}>
              <Text style={styles.headerIcon}>💰</Text>
            </View>
          </div>

          {/* Blue Line */}
          <View style={styles.blueLine} />

          <View style={styles.contentWrapper}>
            {/* Left Column - Form */}
            <View style={styles.formCard}>
              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>
                  {i18n.t("nameofdain")}
                </Text>
                <TextInput
                  style={[styles.input, !edit && styles.inputDisabled]}
                  value={name}
                  onChangeText={setName}
                  editable={edit}
                  placeholderTextColor="#999"
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>
                  {i18n.t("quantityofdette")}
                </Text>
                <TextInput
                  style={[styles.input, !edit && styles.inputDisabled]}
                  value={amount}
                  onChangeText={setAmount}
                  keyboardType="numeric"
                  editable={edit}
                  placeholderTextColor="#999"
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>
                  {i18n.t("phonenumber")}
                </Text>
                <TextInput
                  style={[styles.input, !edit && styles.inputDisabled]}
                  value={phone}
                  onChangeText={setPhone}
                  keyboardType="phone-pad"
                  editable={edit}
                  placeholderTextColor="#999"
                />
              </View>

              {edit && (
                <View style={styles.inputGroup}>
                  <Text style={styles.inputLabel}>
                    {i18n.t("paidamount")}
                  </Text>
                  <TextInput
                    style={styles.input}
                    value={editPaidAmount}
                    onChangeText={setEditPaidAmount}
                    keyboardType="numeric"
                    placeholderTextColor="#999"
                  />
                </View>
              )}

              {/* Edit/Update Buttons */}
              <View style={styles.buttonRow}>
                {edit ? (
                  <>
                    <TouchableOpacity
                      style={styles.saveButton}
                      onPress={handleUpdateDebt}
                      disabled={buttonSpinner}
                    >
                      {buttonSpinner ? (
                        <ActivityIndicator color="#FFF" />
                      ) : (
                        <Text style={styles.saveButtonText}>
                          {i18n.t("save")}
                        </Text>
                      )}
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.cancelButton}
                      onPress={handleCancelEdit}
                    >
                      <Text style={styles.cancelButtonText}>
                        {i18n.t("cancel")}
                      </Text>
                    </TouchableOpacity>
                  </>
                ) : (
                  <>
                    <TouchableOpacity
                      style={styles.editButton}
                      onPress={() => setEdit(true)}
                    >
                      <Text style={styles.editButtonText}>
                        ✏️ {i18n.t("editdain")}
                      </Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.deleteButton}
                      onPress={handleDelete}
                      disabled={deleteSpinner}
                    >
                      {deleteSpinner ? (
                        <ActivityIndicator color="#FFF" />
                      ) : (
                        <Text style={styles.deleteButtonText}>
                          🗑️ {i18n.t("deletedette")}
                        </Text>
                      )}
                    </TouchableOpacity>
                  </>
                )}
              </View>
            </View>

            {/* Right Column - Payment Status */}
            <View style={styles.statusCard}>
              <Text style={styles.statusTitle}>
                {i18n.t("paymentStatus")}
              </Text>

              <View
                style={[
                  styles.statusIndicator,
                  { borderLeftColor: getStatusColor() },
                ]}
              >
                <View style={styles.statusRow}>
                  <Text style={styles.statusLabel}>
                    {i18n.t("total")}:
                  </Text>
                  <Text style={styles.statusValue}>
                    {totalAmount} {i18n.t("currency")}
                  </Text>
                </View>
                <View style={styles.statusRow}>
                  <Text style={styles.statusLabel}>
                    {i18n.t("paidamount")}:
                  </Text>
                  <Text
                    style={[styles.statusValue, { color: getStatusColor() }]}
                  >
                    {paidAmount} {i18n.t("currency")}
                  </Text>
                </View>
                <View style={styles.statusRow}>
                  <Text style={styles.statusLabel}>
                    {i18n.t("remaining")}:
                  </Text>
                  <Text
                    style={[
                      styles.statusValue,
                      { color: remainingAmount > 0 ? "#EF4444" : "#22C55E" },
                    ]}
                  >
                    {remainingAmount} {i18n.t("currency")}
                  </Text>
                </View>

                {/* Progress Bar */}
                <View style={styles.progressContainer}>
                  <View style={styles.progressBar}>
                    <View
                      style={[
                        styles.progressFill,
                        {
                          width: `${Math.min(paymentPercentage, 100)}%`,
                          backgroundColor: getStatusColor(),
                        },
                      ]}
                    />
                  </View>
                  <Text
                    style={[styles.progressText, { color: getStatusColor() }]}
                  >
                    {paymentPercentage.toFixed(1)}% {i18n.t("paid")}
                  </Text>
                </View>
              </View>

              {/* Payment Section */}
              {!edit && (
                <View style={styles.paymentSection}>
                  <TouchableOpacity
                    style={[
                      styles.paymentToggleButton,
                      paymentEdit && styles.paymentToggleActive,
                    ]}
                    onPress={() => setPaymentEdit(!paymentEdit)}
                  >
                    <Text style={styles.paymentToggleText}>
                      {paymentEdit
                        ? i18n.t("cancel")
                        : `💵 ${i18n.t("paidamount")}`}
                    </Text>
                  </TouchableOpacity>

                  {paymentEdit && (
                    <View style={styles.paymentInputContainer}>
                      <TextInput
                        style={styles.paymentInput}
                        placeholder="0"
                        value={extractedValue}
                        onChangeText={setExtractedValue}
                        keyboardType="numeric"
                        placeholderTextColor="#999"
                      />
                      <TouchableOpacity
                        style={styles.confirmPaymentButton}
                        onPress={handlePayment}
                        disabled={buttonSpinner}
                      >
                        {buttonSpinner ? (
                          <ActivityIndicator color="#FFF" />
                        ) : (
                          <Text style={styles.confirmPaymentText}>
                            {i18n.t("confirm")}
                          </Text>
                        )}
                      </TouchableOpacity>
                    </View>
                  )}
                </View>
              )}

              {/* Bill Info */}
              {bill &&
                bill.listofproducts &&
                bill.listofproducts.length > 0 && (
                  <View style={styles.billSection}>
                    <Text style={styles.billTitle}>
                      {i18n.t("invoice")}{" "}
                      {i18n.t("details")}
                    </Text>
                    <View style={styles.billProductsList}>
                      {bill.listofproducts.map(
                        (product: any, index: number) => (
                          <View key={index} style={styles.billProductItem}>
                            <Text style={styles.billProductName}>
                              {product.name}
                            </Text>
                            <Text style={styles.billProductQuantity}>
                              x{product.quantity}
                            </Text>
                            <Text style={styles.billProductPrice}>
                              {product.price} {i18n.t("currency")}
                            </Text>
                          </View>
                        )
                      )}
                    </View>
                  </View>
                )}
            </View>
          </View>
        </ScrollView>
      </View>
    </div>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F5F5F5",
    minHeight: "100vh" as any,
  },
  containerRTL: {
    flexDirection: "row-reverse",
  } as any,
  mainContent: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  scrollView: {
    flex: 1,
    padding: 24,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 16,
    marginBottom: 16,
  },
  headerTitle: {
    fontSize: 28,
    fontWeight: "bold",
    color: "#333",
  },
  headerIconContainer: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: "#FFF3E0",
    alignItems: "center",
    justifyContent: "center",
  },
  headerIcon: {
    fontSize: 32,
  },
  blueLine: {
    height: 4,
    backgroundColor: "#1379A2",
    borderRadius: 2,
    marginBottom: 24,
  },
  contentWrapper: {
    flexDirection: "row",
    gap: 24,
    flexWrap: "wrap",
  },
  formCard: {
    flex: 1,
    minWidth: 300,
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    padding: 24,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  statusCard: {
    flex: 1,
    minWidth: 300,
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    padding: 24,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  inputGroup: {
    marginBottom: 16,
  },
  inputLabel: {
    fontSize: 16,
    fontWeight: "500",
    color: "#666",
    marginBottom: 8,
    textAlign: "right",
  },
  input: {
    backgroundColor: "#F8F8F8",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E0E0E0",
    paddingVertical: 12,
    paddingHorizontal: 16,
    fontSize: 18,
    textAlign: "right",
  },
  inputDisabled: {
    backgroundColor: "#F0F0F0",
    color: "#666",
  },
  buttonRow: {
    flexDirection: "row",
    gap: 12,
    marginTop: 16,
  },
  editButton: {
    flex: 1,
    backgroundColor: "#1379A2",
    borderRadius: 8,
    paddingVertical: 12,
    alignItems: "center",
  },
  editButtonText: {
    color: "#FFF",
    fontSize: 16,
    fontWeight: "600",
  },
  deleteButton: {
    flex: 1,
    backgroundColor: "#EF4444",
    borderRadius: 8,
    paddingVertical: 12,
    alignItems: "center",
  },
  deleteButtonText: {
    color: "#FFF",
    fontSize: 16,
    fontWeight: "600",
  },
  saveButton: {
    flex: 1,
    backgroundColor: "#22C55E",
    borderRadius: 8,
    paddingVertical: 12,
    alignItems: "center",
  },
  saveButtonText: {
    color: "#FFF",
    fontSize: 16,
    fontWeight: "600",
  },
  cancelButton: {
    flex: 1,
    backgroundColor: "#6B7280",
    borderRadius: 8,
    paddingVertical: 12,
    alignItems: "center",
  },
  cancelButtonText: {
    color: "#FFF",
    fontSize: 16,
    fontWeight: "600",
  },
  statusTitle: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#333",
    textAlign: "center",
    marginBottom: 16,
  },
  statusIndicator: {
    borderLeftWidth: 4,
    paddingLeft: 16,
    marginBottom: 20,
  },
  statusRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  statusLabel: {
    fontSize: 16,
    color: "#666",
  },
  statusValue: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#333",
  },
  progressContainer: {
    marginTop: 12,
  },
  progressBar: {
    height: 8,
    backgroundColor: "#E5E7EB",
    borderRadius: 4,
    overflow: "hidden",
  },
  progressFill: {
    height: "100%",
    borderRadius: 4,
  },
  progressText: {
    textAlign: "center",
    marginTop: 8,
    fontSize: 16,
    fontWeight: "600",
  },
  paymentSection: {
    marginTop: 16,
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
    paddingTop: 16,
  },
  paymentToggleButton: {
    backgroundColor: "#22C55E",
    borderRadius: 8,
    paddingVertical: 12,
    alignItems: "center",
  },
  paymentToggleActive: {
    backgroundColor: "#F59E0B",
  },
  paymentToggleText: {
    color: "#FFF",
    fontSize: 18,
    fontWeight: "600",
  },
  paymentInputContainer: {
    marginTop: 12,
    gap: 12,
  },
  paymentInput: {
    backgroundColor: "#F8F8F8",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E0E0E0",
    paddingVertical: 12,
    paddingHorizontal: 16,
    fontSize: 18,
    textAlign: "right",
  },
  confirmPaymentButton: {
    backgroundColor: "#1379A2",
    borderRadius: 8,
    paddingVertical: 12,
    alignItems: "center",
  },
  confirmPaymentText: {
    color: "#FFF",
    fontSize: 18,
    fontWeight: "600",
  },
  billSection: {
    marginTop: 16,
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
    paddingTop: 16,
  },
  billTitle: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#333",
    marginBottom: 12,
    textAlign: "right",
  },
  billProductsList: {
    gap: 8,
  },
  billProductItem: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#F0F0F0",
  },
  billProductName: {
    flex: 1,
    fontSize: 16,
    color: "#333",
    textAlign: "right",
  },
  billProductQuantity: {
    fontSize: 16,
    color: "#666",
    marginHorizontal: 12,
  },
  billProductPrice: {
    fontSize: 16,
    fontWeight: "600",
    color: "#1379A2",
  },
});

export default WebDetteDetailsScreen;
